// Runtime settings of the standalone control-room console, read from `console-config.json` served next to the page
// (never from the build, never a hard-coded council URL; architecture rule 9). The deployer writes it with FER's own
// public hostname, the realm's sign-in client and the offline basemap file.
import type { BasemapConfig } from "../map/basemap";

export interface ConsoleConfig {
  apiBaseUrl: string;
  oidc: { issuer: string; clientId: string } | null;
  basemap: BasemapConfig;
}

/** Development builds only may use http://localhost (review FW11); production builds accept https alone. */
const DEV_ALLOWS_LOCALHOST: boolean = import.meta.env.DEV;

/** https only (http only for localhost in a development build, for a developer running the console locally). */
function secureUrl(value: unknown, base: string, allowLocalHttp: boolean): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const u = new URL(value, base);
    const local = allowLocalHttp && (u.hostname === "localhost" || u.hostname === "127.0.0.1");
    return u.protocol === "https:" || (local && u.protocol === "http:") ? u.href.replace(/\/+$/, "") : null;
  } catch {
    return null;
  }
}

/**
 * Validate `console-config.json`: `api_base_url` (https), optional `oidc` (`issuer` https and `client_id`) and optional
 * `basemap` (`pmtiles_url`, relative to the page or https; `style_url` https). Answers an error in words when unusable.
 */
export function parseConsoleConfig(raw: unknown, pageUrl: string, allowLocalHttp = DEV_ALLOWS_LOCALHOST): ConsoleConfig | string {
  const c = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const apiBaseUrl = secureUrl(c.api_base_url, pageUrl, allowLocalHttp);
  if (!apiBaseUrl) return "console-config.json needs api_base_url: the FER API's https address.";
  let oidc: ConsoleConfig["oidc"] = null;
  if (c.oidc && typeof c.oidc === "object") {
    const o = c.oidc as Record<string, unknown>;
    const issuer = secureUrl(o.issuer, pageUrl, allowLocalHttp);
    if (!issuer || typeof o.client_id !== "string" || !o.client_id) return "console-config.json oidc needs an https issuer and a client_id.";
    oidc = { issuer, clientId: o.client_id };
  }
  const b = (c.basemap && typeof c.basemap === "object" ? c.basemap : {}) as Record<string, unknown>;
  const basemap: BasemapConfig = {};
  const pm = secureUrl(b.pmtiles_url, pageUrl, allowLocalHttp);
  if (pm) basemap.pmtilesUrl = pm;
  const style = secureUrl(b.style_url, pageUrl, allowLocalHttp);
  if (style) basemap.styleUrl = style;
  return { apiBaseUrl, oidc, basemap };
}

export async function loadConsoleConfig(fetchImpl: typeof fetch = fetch, pageUrl = globalThis.location?.href ?? "https://localhost/"): Promise<ConsoleConfig | string> {
  try {
    const res = await fetchImpl(new URL("console-config.json", pageUrl).href, { cache: "no-store" });
    if (!res.ok) return `console-config.json could not be read (HTTP ${res.status}).`;
    return parseConsoleConfig(await res.json(), pageUrl);
  } catch {
    return "console-config.json could not be read.";
  }
}
